/**
 * Die Beute-Pipeline — die eigentliche Maschine des Genres.
 *
 * Sechs Stufen, jede eine reine Funktion, jede einzeln testbar
 * (docs/2026-07-25-architektur.md §4.3):
 *
 *   faelltEtwas() -> kategorie() -> basis() -> seltenheit() -> affixe() -> werte()
 *
 * Ein Gegenstand ist erst herausragend, wenn mehrere unabhängige Würfe
 * gleichzeitig gut ausgehen. Genau das erzeugt das "fast" — den Treibstoff
 * des Genres (Dossier §1.2).
 *
 * Zahlen-Kanon §12.2, hier bindend umgesetzt:
 *   - Anteil 'schlicht' am Bodenloot: 0 %
 *   - gewöhnlicher Gegner: <= 2 % Gegenstands-Drop
 *   - benannter Gegner: 100 %, mindestens 'gezeichnet'
 */

import type { Strom } from '@pg/rng';
import type { Seltenheit } from '@pg/protocol';
import type { Affix, Basis, Gegner, Inhalte, Rang, Slot } from '@pg/content';
import type { AffixInstanz, Gattung, Gegenstand } from './typen.js';

export interface BeuteStroeme {
  beute: Strom;
  affix: Strom;
  zahl: Strom;
}

export interface Fundlage {
  /** Fundtiefe — unser ilvl-Äquivalent. */
  tiefe: number;
  grad: number;
  /** Spürsinn des Spielers als Anteil, z. B. 0.4 für +40 %. */
  spuersinn: number;
  /** Pechzähler je Geprägtem — je höher, desto stärker das Gewicht. */
  pech: Record<string, number>;
  /** Rang des Gegners — Anführer lassen garantiert fallen (Kanon §12.2). */
  rang?: Rang;
}

// ------------------------------------------------------- Stufe 1: fällt etwas?

/**
 * Stufe 1 — fällt überhaupt etwas?
 *
 * Die Chance haengt jetzt am RANG, nicht mehr an einer im Code vergrabenen
 * Beuteklasse. Anfuehrer lassen garantiert fallen; gewoehnliche Gegner selten,
 * aber oft genug, dass die Kanon-Zusage (ein Gegenstand alle 60-90 s) haelt.
 * Werte: packages/content/data/regelwerk.json → beute.chanceJeRang.
 */
export function faelltEtwas(
  gegner: Gegner,
  lage: Fundlage,
  strom: Strom,
  inhalte: Inhalte,
): boolean {
  const r = inhalte.regelwerk.beute;
  const rang: Rang = lage.rang ?? (gegner.benannt ? 'ausklang' : 'gewoehnlich');
  const grund = r.chanceJeRang[rang] ?? 0.01;
  if (grund >= 1) {
    // Auch die sichere Chance zieht, sonst verschiebt sich der Strom.
    strom.zahl();
    return true;
  }
  const gradFaktor = Math.min(
    r.gradFaktorDeckel,
    Math.pow(inhalte.regelwerk.gegner.gradBeute, lage.grad - 1),
  );
  return strom.chance(Math.min(0.9, grund * gradFaktor));
}

// ------------------------------------------------------- Stufe 2: Kategorie

const KATEGORIEN: readonly Gattung[] = ['ausruestung', 'siegel', 'baugut', 'material'];

/**
 * Baugut ist eine eigene Kategorie und fällt auch dann, wenn die
 * Ausrüstungssuche trocken läuft — es gibt also immer Fortschritt am Kontor
 * (Game-Design §10). Das ist der Grund, warum kurze Sessions sich lohnen.
 */
export function kategorie(gegner: Gegner, strom: Strom): Gattung {
  const gewichte =
    gegner.beuteklasse === 'ausklang'
      ? [58, 22, 14, 6]
      : gegner.beuteklasse === 'hoch'
        ? [48, 18, 22, 12]
        : [38, 12, 28, 22];
  return strom.gewichtet(KATEGORIEN, gewichte);
}

// ------------------------------------------------------- Stufe 3: Basis

export function basis(inhalte: Inhalte, lage: Fundlage, strom: Strom): Basis {
  // Nur Basen, deren Basisstufe die Fundtiefe nicht übersteigt.
  const moeglich = inhalte.basen.filter((b) => b.basisstufe <= lage.tiefe);
  const auswahl = moeglich.length > 0 ? moeglich : inhalte.basen.slice(0, 1);
  // Tiefere Basen werden mit der Fundtiefe wahrscheinlicher, ohne die
  // flachen ganz zu verdrängen — sonst verschwindet die Vielfalt.
  const gewichte = auswahl.map((b) => {
    const naehe = 1 + (b.basisstufe / Math.max(1, lage.tiefe)) * 2.4;
    return b.gewicht * naehe;
  });
  return strom.gewichtet(auswahl, gewichte);
}

// ------------------------------------------------------- Stufe 4: Seltenheit

export function seltenheit(inhalte: Inhalte, lage: Fundlage, strom: Strom): Seltenheit {
  // Kanon §12.2: 'schlicht' fällt nie auf den Boden.
  const stufen = inhalte.seltenheiten.filter((s) => s.faelltAufBoden);

  // Spürsinn wirkt NUR auf die Seltenheit, nie auf die Menge (Game-Design §6.4),
  // mit abnehmendem Ertrag ab 120 % und Deckel bei 250 %.
  const rb = inhalte.regelwerk.beute;
  const roh = Math.max(0, lage.spuersinn);
  const wirksam =
    roh <= rb.spuersinnAbnahmeAb
      ? roh
      : rb.spuersinnAbnahmeAb + (roh - rb.spuersinnAbnahmeAb) * rb.spuersinnAbnahmeFaktor;
  const gedeckelt = Math.min(rb.spuersinnDeckel, wirksam);

  const gewichte = stufen.map((s) => {
    if (s.id === 'gezeichnet') return s.gewicht;
    // Je seltener die Stufe, desto stärker greift Spürsinn.
    const hebel = s.id === 'verschriftet' ? 0.55 : s.id === 'gepraegt' ? 1.0 : 1.25;
    return s.gewicht * (1 + gedeckelt * hebel);
  });

  return strom.gewichtet(stufen, gewichte).id as Seltenheit;
}

// ------------------------------------------------------- Stufe 5: Affixe

/**
 * Prägelevel aus Fundtiefe und Basisstufe. Begrenzt, welche Affix-Stufen
 * überhaupt erscheinen können — unser alvl-Äquivalent.
 */
export function praegelevel(tiefe: number, basisstufe: number): number {
  return Math.max(1, Math.floor(tiefe * 0.85 + basisstufe * 0.35));
}

function hoechsteStufe(affix: Affix, plevel: number): number {
  let gefunden = -1;
  for (let i = 0; i < affix.stufen.length; i++) {
    if ((affix.stufen[i] as { abPraegelevel: number }).abPraegelevel <= plevel) gefunden = i;
    else break;
  }
  return gefunden;
}

export function affixe(
  inhalte: Inhalte,
  b: Basis,
  stufe: Seltenheit,
  lage: Fundlage,
  stroeme: BeuteStroeme,
): AffixInstanz[] {
  const def = inhalte.seltenheiten.find((s) => s.id === stufe);
  if (!def || def.affixeMax === 0) return [];

  const plevel = praegelevel(lage.tiefe, b.basisstufe);
  const anzahl = stroeme.affix.ganz(def.affixeMin, def.affixeMax);

  const praefixeMax = def.praefixeMax ?? 0;
  const suffixeMax = def.suffixeMax ?? 0;

  const gewaehlt: AffixInstanz[] = [];
  const belegteGruppen = new Set<string>();
  let praefixe = 0;
  let suffixe = 0;

  for (let i = 0; i < anzahl; i++) {
    // Kandidaten: passender Slot, Gruppe noch frei, mindestens eine Stufe erreichbar.
    const kandidaten: Affix[] = [];
    const gewichte: number[] = [];

    const quelle = [...inhalte.praefixe, ...inhalte.suffixe];
    for (const a of quelle) {
      if (!a.slots.includes(b.slot as Slot)) continue;
      if (belegteGruppen.has(a.gruppe)) continue;
      if (a.art === 'praefix' && praefixe >= praefixeMax) continue;
      if (a.art === 'suffix' && suffixe >= suffixeMax) continue;
      if (hoechsteStufe(a, plevel) < 0) continue;
      kandidaten.push(a);
      gewichte.push(a.gewicht);
    }
    if (kandidaten.length === 0) break;

    const a = stroeme.affix.gewichtet(kandidaten, gewichte);

    // Stufe: gewichtet in Richtung der hoechsten erreichbaren, aber nicht garantiert.
    // Das erzeugt das "fast" — eine gute Stufe ist die Ausnahme, nicht die Regel.
    const max = hoechsteStufe(a, plevel);
    const stufenGewichte: number[] = [];
    for (let s = 0; s <= max; s++) stufenGewichte.push(Math.pow(1.55, s));
    const indizes = stufenGewichte.map((_, idx) => idx);
    const gewaehlteStufe = stroeme.affix.gewichtet(indizes, stufenGewichte);

    const sd = a.stufen[gewaehlteStufe] as {
      min: number;
      max: number;
      wortmarke: string;
    };

    // Stufe 6: die Zahl innerhalb der Spanne — eigener Strom, eigener Wurf.
    const betrag = werte(sd.min, sd.max, a.wirkung.art, stroeme.zahl);

    gewaehlt.push({
      affix: a.id,
      wert: a.wirkung.wert,
      art: a.wirkung.art,
      betrag,
      stufe: gewaehlteStufe + 1,
      wortmarke: sd.wortmarke,
    });
    belegteGruppen.add(a.gruppe);
    if (a.art === 'praefix') praefixe++;
    else suffixe++;
  }

  // Deterministische Reihenfolge: erst Präfixe, dann Suffixe, jeweils nach id.
  gewaehlt.sort((x, y) => x.affix.localeCompare(y.affix));
  return gewaehlt;
}

// ------------------------------------------------------- Stufe 6: Werte

export function werte(min: number, max: number, art: 'flach' | 'anteil', strom: Strom): number {
  const roh = strom.spanne(min, max);
  // Flache Werte sind ganzzahlig, Anteile auf drei Stellen — sonst stehen
  // im Tooltip Zahlen wie 0.13400000000000001.
  return art === 'flach' ? Math.round(roh) : Math.round(roh * 1000) / 1000;
}

// ------------------------------------------------------- Zusammenbau

/** Name aus Basisname und Wortmarken: "Gehärteter Zunfthammer der Esse". */
export function baueName(basisName: string, affixe: readonly AffixInstanz[]): string {
  const praefix = affixe.find((a) => !a.wortmarke.startsWith('de'));
  const suffix = affixe.find((a) => a.wortmarke.startsWith('de'));
  const teile: string[] = [];
  if (praefix) teile.push(praefix.wortmarke);
  teile.push(basisName);
  if (suffix) teile.push(suffix.wortmarke);
  return teile.join(' ');
}

export interface BeuteErgebnis {
  gattung: Gattung;
  seltenheit: Seltenheit;
  name: string;
  menge: number;
  gegenstand: Gegenstand | null;
}

/**
 * Ein vollständiger Drop. `naechsteId` wird vom Aufrufer geliefert, damit die
 * Simulation die einzige Stelle bleibt, die IDs vergibt.
 */
export function wuerfleBeute(
  inhalte: Inhalte,
  gegner: Gegner,
  lage: Fundlage,
  stroeme: BeuteStroeme,
  naechsteId: () => number,
): BeuteErgebnis | null {
  if (!faelltEtwas(gegner, lage, stroeme.beute, inhalte)) return null;

  const gattung = kategorie(gegner, stroeme.beute);

  if (gattung !== 'ausruestung') {
    const menge =
      gattung === 'siegel'
        ? 1
        : stroeme.beute.ganz(1, gattung === 'baugut' ? 2 : 4);
    return {
      gattung,
      seltenheit: 'gezeichnet',
      name: nameFuerGattung(gattung, lage.tiefe, stroeme.beute),
      menge,
      gegenstand: null,
    };
  }

  const b = basis(inhalte, lage, stroeme.beute);
  let stufe = seltenheit(inhalte, lage, stroeme.beute);

  // Geprägte und Reliquien sind Einzelstücke — nur, wenn es für diese Basis
  // überhaupt eines gibt. Sonst fällt es auf 'verschriftet' zurück.
  if (stufe === 'gepraegt' || stufe === 'reliquie') {
    const moeglich = inhalte.gepraegte.filter(
      (g) => g.basis === b.id && g.basisstufe <= lage.tiefe,
    );
    if (moeglich.length === 0) {
      stufe = 'verschriftet';
    } else {
      // Pechschutz: je länger eine Quelle dieses Stück nicht ausgeworfen hat,
      // desto stärker sein Gewicht (Game-Design §6.5). Nur hier oben, nie
      // auf gewöhnlicher Beute.
      const gewichte = moeglich.map((g) => g.gewicht * (1 + (lage.pech[g.id] ?? 0) * 0.02));
      const g = stroeme.beute.gewichtet(moeglich, gewichte);
      const gegenstand = baueGepraegtes(g, b, lage, stroeme, naechsteId);
      return {
        gattung: 'ausruestung',
        seltenheit: stufe,
        name: g.name,
        menge: 1,
        gegenstand,
      };
    }
  }

  const affixListe = affixe(inhalte, b, stufe, lage, stroeme);
  const name = baueName(b.name, affixListe);

  const gegenstand: Gegenstand = {
    id: naechsteId(),
    basis: b.id,
    name,
    slot: b.slot,
    seltenheit: stufe,
    fundtiefe: lage.tiefe,
    praegelevel: praegelevel(lage.tiefe, b.basisstufe),
    affixe: affixListe,
    grundwerte: { ...b.grundwerte },
    fassungen: 0,
    bindung: b.bindung,
    bindungMax: b.bindung,
  };

  return { gattung: 'ausruestung', seltenheit: stufe, name, menge: 1, gegenstand };
}

function baueGepraegtes(
  g: { id: string; name: string; beschreibung: string; fest: readonly { wert: string; art: string; wert_min: number; wert_max: number }[]; wild: { wert: string; art: string; wert_min: number; wert_max: number } },
  b: Basis,
  lage: Fundlage,
  stroeme: BeuteStroeme,
  naechsteId: () => number,
): Gegenstand {
  const affixListe: AffixInstanz[] = g.fest.map((f) => ({
    affix: `fest:${f.wert}`,
    wert: f.wert as AffixInstanz['wert'],
    art: f.art as 'flach' | 'anteil',
    betrag: f.wert_min,
    stufe: 0,
    wortmarke: '',
  }));

  // Genau EINE wild gewürfelte Eigenschaft — dieselbe Prägung ist nie zweimal
  // gleich (Game-Design §6.2).
  affixListe.push({
    affix: `wild:${g.wild.wert}`,
    wert: g.wild.wert as AffixInstanz['wert'],
    art: g.wild.art as 'flach' | 'anteil',
    betrag: werte(g.wild.wert_min, g.wild.wert_max, g.wild.art as 'flach' | 'anteil', stroeme.zahl),
    stufe: 0,
    wortmarke: '',
  });

  return {
    id: naechsteId(),
    basis: b.id,
    name: g.name,
    slot: b.slot,
    seltenheit: 'gepraegt',
    fundtiefe: lage.tiefe,
    praegelevel: praegelevel(lage.tiefe, b.basisstufe),
    affixe: affixListe,
    grundwerte: { ...b.grundwerte },
    fassungen: 0,
    bindung: b.bindung,
    bindungMax: b.bindung,
    gepraegtId: g.id,
    beschreibung: g.beschreibung,
  };
}

const SIEGEL_NAMEN = [
  'Kerbsiegel', 'Wachssiegel', 'Bleimarke', 'Zunftstempel', 'Zollsiegel',
  'Essemarke', 'Frachtsiegel', 'Lohnstempel', 'Kammersiegel', 'Meistersiegel',
  'Ofensiegel', 'Zehntmarke', 'Grubensiegel', 'Erstes Siegel',
];

const BAUGUT_NAMEN = [
  'Gebrannter Ziegel', 'Angeleisen', 'Klarglas', 'Kartenpapier',
  'Laufendes Uhrwerk', 'Unversehrter Blasebalg', 'Fassdauben', 'Schmiedekohle',
];

const MATERIAL_NAMEN = ['Rohgut', 'Schlackenstaub', 'Glutasche', 'Feilspäne'];

function nameFuerGattung(gattung: Gattung, tiefe: number, strom: Strom): string {
  if (gattung === 'siegel') {
    // Höhere Ränge erst mit der Tiefe — das ist die Chase-Währung.
    const maxRang = Math.min(SIEGEL_NAMEN.length, 2 + Math.floor(tiefe / 6));
    const gewichte: number[] = [];
    for (let i = 0; i < maxRang; i++) gewichte.push(Math.pow(0.55, i));
    const indizes = gewichte.map((_, i) => i);
    return SIEGEL_NAMEN[strom.gewichtet(indizes, gewichte)] as string;
  }
  if (gattung === 'baugut') return strom.waehle(BAUGUT_NAMEN);
  return strom.waehle(MATERIAL_NAMEN);
}
